/**
 * Client Portal V2 - Job Detail Page
 * ===================================
 * Single source of truth for a job.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiClient, clientAuth } from "@/lib/clientApi";
import ClientLayout from "@/components/client/ClientLayout";
import {
  ClientStatusBadge, ClientNextActionBanner, ClientTimeline,
  ClientDocumentItem, ClientLoadingState, ClientErrorState
} from "@/components/client/ClientComponents";
import {
  ArrowLeft, Building2, Calendar, User, Clock, FileText,
  CheckCircle, AlertTriangle, Download, ExternalLink, Receipt
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function ClientJobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [alerts, setAlerts] = useState([]);

  const fetchJob = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      const res = await apiClient.get(`/api/client/jobs/${jobId}`);
      
      if (res.data?.success) {
        setJob(res.data.data);
        setAlerts(res.data.alerts || []);
      }
    } catch (err) {
      console.error("Failed to load job:", err);
      if (err.response?.status === 401) {
        clientAuth.logout();
        return;
      }
      if (err.response?.status === 404) {
        setError("Job not found");
      } else {
        setError("Failed to load job details");
      }
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (!clientAuth.isAuthenticated()) {
      navigate("/client-login");
      return;
    }
    fetchJob();
  }, [fetchJob, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return `£${Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <ClientLayout>
        <ClientLoadingState message="Loading job details..." />
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <ClientErrorState message={error} onRetry={fetchJob} />
      </ClientLayout>
    );
  }

  if (!job) return null;

  const isOverdue = job.due_date && 
    !["COMPLETED", "CLOSED", "CANCELLED", "REJECTED"].includes(job.stage) &&
    new Date(job.due_date) < new Date();

  return (
    <ClientLayout>
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/client-v2/jobs")}
        className="mb-4 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Jobs
      </Button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-slate-900">{job.job_reference}</h1>
              <ClientStatusBadge stage={job.stage} label={job.stage_label} />
              {isOverdue && (
                <Badge className="bg-red-100 text-red-700">Overdue</Badge>
              )}
            </div>
            <p className="text-slate-600">{job.description}</p>
          </div>
          
          <div className="flex gap-2">
            {job.quote && (
              <Link to={`/client-v2/quotes/${job.quote.id}`}>
                <Button variant="outline" size="sm">
                  <FileText className="w-4 h-4 mr-2" />
                  View Quote
                </Button>
              </Link>
            )}
            {job.invoice && (
              <Link to={`/client-v2/invoices/${job.invoice.id}`}>
                <Button variant="outline" size="sm">
                  <Receipt className="w-4 h-4 mr-2" />
                  View Invoice
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2 mb-6">
          {alerts.map((alert, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-3 p-4 rounded-xl ${
                alert.severity === "critical"
                  ? "bg-red-50 border border-red-200"
                  : alert.severity === "high"
                  ? "bg-orange-50 border border-orange-200"
                  : "bg-yellow-50 border border-yellow-200"
              }`}
            >
              <AlertTriangle className={`w-5 h-5 ${
                alert.severity === "critical" ? "text-red-600" : "text-orange-600"
              }`} />
              <span className="text-sm font-medium">{alert.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Next Action Banner */}
      {job.next_action && (
        <div className="mb-6">
          <ClientNextActionBanner
            action={job.next_action.action}
            owner={job.next_action.owner}
            urgent={job.next_action.urgent}
            link={job.stage === "AWAITING_CLIENT_APPROVAL" && job.quote 
              ? `/client-v2/quotes/${job.quote.id}` 
              : null}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Job Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Job Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Park / Site</p>
                  <p className="font-medium text-slate-900">{job.park_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Unit / Asset</p>
                  <p className="font-medium text-slate-900">{job.unit_name || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Job Type</p>
                  <p className="font-medium text-slate-900">{job.job_type || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Priority</p>
                  <p className="font-medium text-slate-900">{job.priority}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Contractor</p>
                  <p className="font-medium text-slate-900">{job.contractor_name || "Not Assigned"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Due Date</p>
                  <p className={`font-medium ${isOverdue ? "text-red-600" : "text-slate-900"}`}>
                    {formatDate(job.due_date)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Scheduled Date</p>
                  <p className="font-medium text-slate-900">{formatDate(job.scheduled_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Quote Value</p>
                  <p className="font-medium text-slate-900">{formatCurrency(job.quote_value)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quote Summary */}
          {job.quote && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quote Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-slate-500">Reference</p>
                    <p className="font-medium text-slate-900">{job.quote.reference}</p>
                  </div>
                  <ClientStatusBadge 
                    stage={job.quote.status === "Approved" ? "APPROVED" : job.quote.status === "Rejected" ? "REJECTED" : "AWAITING_CLIENT_APPROVAL"} 
                    label={job.quote.status} 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Subtotal</p>
                    <p className="font-medium text-slate-900">{formatCurrency(job.quote.subtotal)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total (inc VAT)</p>
                    <p className="font-semibold text-slate-900 text-lg">{formatCurrency(job.quote.total)}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <Link to={`/client-v2/quotes/${job.quote.id}`}>
                    <Button className="w-full">
                      {job.quote.status === "Approved" ? "View Quote Details" : "Review & Approve Quote"}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Completion Evidence */}
          {["COMPLETED", "AWAITING_DOCUMENTS", "READY_TO_INVOICE", "INVOICED", "CLOSED"].includes(job.stage) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Completion Evidence</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Job Sheet</span>
                    {job.has_job_sheet ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" /> Uploaded
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">
                        <AlertTriangle className="w-3 h-3 mr-1" /> Missing
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Before Photos</span>
                    {job.has_before_photos ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" /> Uploaded
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600">Not Required</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">After Photos</span>
                    {job.has_after_photos ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" /> Uploaded
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600">Not Required</Badge>
                    )}
                  </div>
                  {job.requires_certificate && (
                    <div className="flex items-center justify-between py-2">
                      <span className="text-slate-600">Certificate</span>
                      {job.has_certificate ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" /> Uploaded
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">
                          <AlertTriangle className="w-3 h-3 mr-1" /> Required
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Documents */}
          {job.documents && job.documents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {job.documents.map((doc) => (
                    <ClientDocumentItem key={doc.id} doc={doc} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Timeline */}
        <div className="space-y-6">
          {/* Status Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {["NEW", "AWAITING_QUOTE", "AWAITING_CLIENT_APPROVAL", "APPROVED", "SCHEDULED", "IN_PROGRESS", "COMPLETED", "CLOSED"].map((stage, idx) => {
                  const isCurrent = job.stage === stage;
                  const isPast = job.stage_order > (idx + 1);
                  
                  return (
                    <div key={stage} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        isCurrent ? "bg-teal-500 ring-4 ring-teal-100" :
                        isPast ? "bg-teal-500" : "bg-slate-200"
                      }`} />
                      <span className={`text-sm ${
                        isCurrent ? "font-semibold text-teal-700" :
                        isPast ? "text-slate-700" : "text-slate-400"
                      }`}>
                        {stage.replace(/_/g, " ")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Key Dates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Key Dates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Created</p>
                    <p className="text-sm font-medium">{formatDate(job.created_at)}</p>
                  </div>
                </div>
                {job.scheduled_date && (
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Scheduled</p>
                      <p className="text-sm font-medium">{formatDate(job.scheduled_date)}</p>
                    </div>
                  </div>
                )}
                {job.completed_at && (
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <div>
                      <p className="text-xs text-slate-500">Completed</p>
                      <p className="text-sm font-medium">{formatDate(job.completed_at)}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Last Updated</p>
                    <p className="text-sm font-medium">{formatDate(job.updated_at)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <ClientTimeline items={job.timeline} />
            </CardContent>
          </Card>
        </div>
      </div>
    </ClientLayout>
  );
}
